/**
 * Main API service for the application
 * Centralized HTTP client with error handling and authentication
 */

import tokenManager from './tokenManager'

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5173/api'
const API_VERSION = 'v1'

// Default headers
const getDefaultHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
  
  // Add auth token if available
  try {
    const token = await tokenManager.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  } catch (error) {
    console.error('Failed to get access token for API request:', error)
    // Don't throw here, let the API call proceed without auth
    // The server will return 401 if auth is required
  }
  
  return headers
}

// HTTP client with error handling
class ApiClient {
  constructor() {
    // this.baseURL = `${API_BASE_URL}/${API_VERSION}`
    this.baseURL = `${API_BASE_URL}`
  }

  /**
   * Make HTTP request with error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    // Build initial request config with current token (if any)
    const config = {
      headers: await getDefaultHeaders(),
      ...options,
    }

    try {
      let response = await fetch(url, config)

      // If unauthorized/forbidden, attempt a one-time token refresh and retry
      // Rule applied: simple solution; avoid duplication; security best practice
      if (response.status === 401 || response.status === 403) {
        try {
          // Trigger single-flight refresh so concurrent requests share the same refresh
          await tokenManager.refreshToken()
          // Rebuild headers after refresh to include the fresh token
          const retryHeaders = await getDefaultHeaders()
          response = await fetch(url, { ...options, headers: retryHeaders })
        } catch (refreshError) {
          // If refresh fails, propagate the original auth error below
          console.error('Token refresh failed, not retrying request:', refreshError)
        }
      }

      // Handle non-OK responses uniformly after optional retry
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      // Parse response
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }

      return await response.text()
    } catch (error) {
      console.error('API Request failed:', error)
      throw error
    }
  }

  // GET request
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint
    
    return this.request(url, {
      method: 'GET',
    })
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // PATCH request
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    })
  }

  /**
   * Fetch dashboard investment metrics
   * @param {string} metrics - Comma-separated list of metrics (total-invested, portfolio-companies, total-deals)
   * @returns {Promise<Object>} Dashboard metrics data
   */
  async getDashboardMetrics(metrics = 'total-invested,portfolio-companies,total-deals') {
    return this.get('/dash/investment/', { metrics })
  }

  /**
   * Fetch investment timeline data
   * @param {Object} params - Query parameters for filtering investments
   * @returns {Promise<Object>} Investment list with timeline data
   */
  async getInvestments(params = {}) {
    return this.get('/investment/', params)
  }

  /**
   * Fetch follow-on opportunities
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Follow-ons list for the authenticated user
   */
  async getFollowOns(params = {}) {
    // Endpoint returns paginated results with shape matching FollowOnsList
    // See instructions/Backend-API-schema.yml -> /api/follow-ons/
    return this.get('/follow-ons/', params)
  }

  /**
   * Fetch investor updates for dashboard
   * @param {Object} params - Optional query parameters (limit, offset)
   * @returns {Promise<Object>} Updates list with shape matching StartupInvestorUpdateList
   */
  async getUpdates(params = {}) {
    // Endpoint returns paginated results with shape matching StartupInvestorUpdateList
    // See instructions/Backend-API-schema.yml -> /api/dash/updates/
    return this.get('/dash/updates/', params)
  }

  /**
   * Fetch individual investor update by startup ID and update ID
   * @param {string} startupId - Startup identifier
   * @param {string} updateId - Update identifier
   * @returns {Promise<Object>} Full update details with shape matching StartupInvestorUpdate
   */
  async getUpdate(startupId, updateId) {
    // Endpoint returns full update details including fullMessage
    // See instructions/Backend-API-schema.yml -> /api/startups/{startupID}/updates/{updateID}/
    return this.get(`/startups/${startupId}/updates/${updateId}/`)
  }

  /**
   * Fetch documents list
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Documents list
   */
  async getDocuments(params = {}) {
    // Endpoint returns list of documents with metadata
    // See instructions/Backend-API-schema.yml -> /api/documents/
    return this.get('/documents/', params)
  }

  /**
   * Get pre-signed download URL for document
   * @param {string} documentId - Document identifier
   * @returns {Promise<Object>} Pre-signed URL with shape matching DocumentLink
   */
  async getDocumentDownload(documentId) {
    // Endpoint returns pre-signed download URL
    // See instructions/Backend-API-schema.yml -> /api/documents/{id}/download/
    return this.get(`/documents/${documentId}/download/`)
  }

  /**
   * Fetch list of startups with filtering support
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.industry - Filter by industry
   * @param {string} params.companyName - Filter by company name
   * @param {string} params.hqProvince - Filter by headquarters province
   * @param {string} params.search - General search term
   * @param {number} params.limit - Number of results per page
   * @param {number} params.offset - Initial index for pagination
   * @returns {Promise<Object>} Startup list with shape matching StartupList
   */
  async getStartups(params = {}) {
    // Endpoint returns paginated results with shape matching StartupList
    // Supports filtering by industry, companyName, hqProvince, and search
    // See instructions/Backend-API-schema.yml -> /api/startups/
    return this.get('/startups/', params)
  }

  /**
   * Fetch individual startup details
   * @param {string} startupId - Startup identifier
   * @returns {Promise<Object>} Startup details with shape matching Startup
   */
  async getStartup(startupId) {
    // Endpoint returns startup details with shape matching Startup
    // See instructions/Backend-API-schema.yml -> /api/startups/{startupID}/
    return this.get(`/startups/${startupId}/`)
  }

  /**
   * Fetch company-specific investment metrics
   * @param {string} startupId - Startup identifier
   * @param {string} metrics - Comma-separated list of metrics (investment, investment-count, investment-latest-date)
   * @returns {Promise<Object>} Company metrics with shape matching CompanyMetrics
   */
  async getCompanyMetrics(startupId, metrics = 'investment,investment-count,investment-latest-date') {
    // Endpoint returns startup-specific investment metrics
    // See instructions/Backend-API-schema.yml -> /api/dash/company/{startupID}/
    return this.get(`/dash/company/${startupId}/`, { metrics })
  }

  /**
   * Fetch investments filtered by startup ID
   * @param {string} startupId - Startup identifier
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} Investment list filtered by startup
   */
  async getInvestmentsByStartup(startupId, params = {}) {
    // Endpoint returns investments filtered by StartupID
    // See instructions/Backend-API-schema.yml -> /api/investment/
    return this.get('/investment/', { StartupId: startupId, ...params })
  }

  /**
   * Fetch startup updates
   * @param {string} startupId - Startup identifier
   * @param {Object} params - Optional query parameters (limit, offset)
   * @returns {Promise<Object>} Startup updates list with shape matching StartupInvestorUpdateList
   */
  async getStartupUpdates(startupId, params = {}) {
    // Endpoint returns startup-specific investor updates
    // See instructions/Backend-API-schema.yml -> /api/startups/{startupID}/updates/
    return this.get(`/startups/${startupId}/updates/`, params)
  }

  /**
   * Fetch follow-on opportunities for a specific startup
   * @param {string} startupId - Startup identifier
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Follow-ons list for the specific startup
   */
  async getStartupFollowOns(startupId, params = {}) {
    // Endpoint returns follow-on opportunities for a specific startup
    // See instructions/Backend-API-schema.yml -> /api/startups/{startupID}/follow-ons/
    return this.get(`/startups/${startupId}/follow-ons/`, params)
  }

  /**
   * Fetch follow-on opportunities
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Follow-ons list with shape matching FollowOnsList
   */
  async getFollowOnOpportunities(params = {}) {
    // Endpoint returns follow-on opportunities for the authenticated user
    // See instructions/Backend-API-schema.yml -> /api/follow-ons/
    return this.get('/follow-ons/', params)
  }

  /**
   * Fetch current user's profile
   * @returns {Promise<Object>} User profile with shape matching ApiUserProfile
   */
  async getUserProfile() {
    // Endpoint returns current user's profile (email, first_name, last_name)
    // See instructions/Backend-API-schema.yml -> /api/users/me/
    return this.get('/users/me/')
  }

  /**
   * Update current user's profile
   * @param {Object} profileData - Profile data to update
   * @param {string} profileData.email - User's email address
   * @param {string} profileData.first_name - User's first name
   * @param {string} profileData.last_name - User's last name
   * @returns {Promise<Object>} Updated user profile
   */
  async updateUserProfile(profileData) {
    // Endpoint accepts subset of fields (email, first_name, last_name)
    // See instructions/Backend-API-schema.yml -> /api/users/me/ PATCH
    return this.patch('/users/me/', profileData)
  }
}

// Create and export API client instance
const apiClient = new ApiClient()

export default apiClient
